import { z } from 'zod';
import { currencySchema, moneySchema } from './common.js';
export const projectContractInputSchema = z
    .object({
    startDate: z.iso.date().nullable(),
    endDate: z.iso.date().nullable(),
    contractNumber: z.string().trim().max(128).nullable(),
    agreementName: z.string().trim().max(240).nullable().optional(),
    purchaseOrderNumber: z.string().trim().max(128).nullable(),
    googleDriveFolderUrl: z
        .string()
        .trim()
        .max(512)
        .url()
        .refine(isGoogleDriveFolderUrl, 'A valid Google Drive folder URL is required.')
        .overwrite(normalizeGoogleDriveFolderUrl)
        .nullable(),
    currency: currencySchema,
})
    .refine((value) => !value.startDate || !value.endDate || value.startDate <= value.endDate, {
    message: 'Project start date must precede end date.',
    path: ['endDate'],
});
export const contractSettingsSchema = z
    .object({
    warningDays: z.number().int().min(1).max(3650),
    criticalDays: z.number().int().min(1).max(3650),
})
    .refine((value) => value.warningDays >= value.criticalDays, {
    path: ['warningDays'],
    message: 'Warning days must be greater than or equal to critical days.',
});
export const contractPeriodHealthSchema = z.object({
    state: z.enum(['missing', 'active', 'warning', 'critical', 'expired']),
    daysUntilEnd: z.number().int().nullable(),
});
export const contractCompletenessSchema = z.object({
    complete: z.boolean(),
    missing: z.array(z.enum(['signature', 'drive_folder', 'period', 'tempo_account'])),
});
export const projectContractSummarySchema = z.object({
    contractSigned: z.boolean(),
    currency: currencySchema,
    signedBudget: moneySchema,
    periodHealth: contractPeriodHealthSchema,
    completeness: contractCompletenessSchema,
    googleDriveFolderUrl: z.url().nullable(),
});
export const projectHistoryChangeSchema = z.object({
    field: z.enum([
        'startDate',
        'endDate',
        'contractNumber',
        'purchaseOrderNumber',
        'currency',
        'contractSigned',
        'googleDriveFolderUrl',
        'agreementName',
        'statusOverride',
        'productionStartedOverride',
        'productionOverrideReason',
    ]),
    before: z.union([z.string(), z.boolean(), z.null()]),
    after: z.union([z.string(), z.boolean(), z.null()]),
});
export const projectHistoryEntrySchema = z.object({
    id: z.uuid(),
    action: z.string().min(1),
    actorName: z.string().min(1),
    createdAt: z.iso.datetime(),
    changes: z.array(projectHistoryChangeSchema),
});
export const projectStateInputSchema = z
    .object({
    updatedAt: z.iso.datetime(),
    status: z.enum(['planning', 'active', 'paused', 'completed', 'archived']).nullable().optional(),
    contractSigned: z.boolean().optional(),
    productionStartedOverride: z.boolean().nullable().optional(),
    reason: z.string().trim().min(1).max(500).optional(),
})
    .superRefine((value, ctx) => {
    if (typeof value.productionStartedOverride === 'boolean' && !value.reason) {
        ctx.addIssue({
            code: 'custom',
            path: ['reason'],
            message: 'Expliquez la dérogation au calcul automatique.',
        });
    }
    if (value.status === undefined &&
        value.contractSigned === undefined &&
        value.productionStartedOverride === undefined) {
        ctx.addIssue({ code: 'custom', message: 'Aucun changement demandé.' });
    }
});
export const projectStateResponseSchema = z.object({
    data: z.object({
        id: z.uuid(),
        status: z.enum(['planning', 'active', 'paused', 'completed', 'archived']),
        contractSigned: z.boolean(),
        productionStarted: z.boolean(),
        productionStatusSource: z.enum(['automatic', 'override']),
        updatedAt: z.iso.datetime(),
    }),
});
function googleDriveFolderId(value) {
    const url = new URL(value);
    if (url.protocol !== 'https:' || url.hostname !== 'drive.google.com')
        return undefined;
    return url.pathname.match(/^\/drive(?:\/u\/\d+)?\/folders\/([A-Za-z0-9_-]+)\/?$/u)?.[1];
}
function isGoogleDriveFolderUrl(value) {
    return googleDriveFolderId(value) !== undefined;
}
function normalizeGoogleDriveFolderUrl(value) {
    return `https://drive.google.com/drive/folders/${googleDriveFolderId(value)}`;
}
//# sourceMappingURL=project-contracts.js.map